<template>
  <div class="audience-cta-wrapper">
    <div class="container">
      <p class="top-msg">
        {{ $t('thank_for_participation') }}
      </p>

      <template v-if="audienceSignUpInvitationCtaVariant">
        <p v-if="ctaSubtitle" class="sub-msg">
          {{ ctaSubtitle }}
        </p>

        <div v-if="isShowAhaSection" class="aha-primary-cta">
          <button
            class="cta-button-primary"
            data-testid="audience-cta-get-ahaslides-button"
            @click="openTrialLink"
          >
            <aha-logo
              :icon-only="true"
              :useDefaultColor="false"
              class="btn-aha-logo"
              width="22"
              height="22"
            />
            <span>{{ ctaButtonLabel }}</span>
          </button>
        </div>

        <div
          v-if="isShowAhaSection && isShownCTAButtons"
          class="or-divider"
        >
          <span class="or-text">{{ $t('or') }}</span>
        </div>

        <div v-if="isShownCTAButtons" class="audience-cta-buttons" :class="{ 'audience-cta-buttons--gap': !isShowAhaSection }">
          <aha-antd-button
            v-if="isEnableSlideReview"
            class="cta-button"
            variant="secondary"
            iconName="presenter-light"
            iconWidth="16"
            iconHeight="16"
            iconViewBox="0 0 16 18"
            data-testid="audience-footer-review-slides-button"
            @click="openTabLookback"
          >
            <span>{{ $t('review_slides') }}</span>
          </aha-antd-button>

          <aha-antd-button
            v-if="isEnableFeedback"
            class="cta-button"
            variant="secondary"
            :disabled="isDisabledFeedbackButton"
            iconName="feedback"
            iconWidth="16"
            iconHeight="16"
            iconViewBox="0 0 18 18"
            data-testid="audience-footer-give-feedback-button"
            @click="openTabFeedback"
          >
            <span>{{ $t('give_feedback') }}</span>
          </aha-antd-button>
        </div>
      </template>

      <template v-else>
        <div v-if="isShownCTAButtons" class="audience-cta-buttons audience-cta-buttons--gap">
          <aha-antd-button
            v-if="isEnableSlideReview"
            class="cta-button"
            variant="secondary"
            iconName="presenter-light"
            iconWidth="16"
            iconHeight="16"
            iconViewBox="0 0 16 18"
            data-testid="audience-footer-review-slides-button"
            @click="openTabLookback"
          >
            <span>{{ $t('review_slides') }}</span>
          </aha-antd-button>

          <aha-antd-button
            v-if="isEnableFeedback"
            class="cta-button"
            variant="secondary"
            :disabled="isDisabledFeedbackButton"
            iconName="feedback"
            iconWidth="16"
            iconHeight="16"
            iconViewBox="0 0 18 18"
            data-testid="audience-footer-give-feedback-button"
            @click="openTabFeedback"
          >
            <span>{{ $t('give_feedback') }}</span>
          </aha-antd-button>
        </div>

        <button
          v-if="isShowAhaSection"
          class="audience-cta-link"
          data-testid="audience-footer-get-ahaslides-free-button"
          @click="openTrialLink"
        >
          <div class="aha-icon-wrapper">
            <aha-logo
              :icon-only="true"
              :useDefaultColor="true"
              class="img-link"
              width="26"
              height="26"
            />
          </div>
          <span class="text-link">
            {{ $t('get_ahaslides_free') }}
          </span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import util from '@/util'
import config from '@/config'

const CTA_VARIANT_GET_AHASLIDES_FOR_FREE = 'get-ahaslides-for-free'
const CTA_VARIANT_HOST_EVENT_FOR_FREE = 'host-event-for-free'

export default {
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      slides: (state) => state.slides,
    }),
    ...mapGetters(['isEnableFeedback', 'isEnableSlideReview']),
    ...mapGetters('presentationSettings', ['audienceSignUpInvitationCtaVariant']),
    ctaSubtitle() {
      if (this.audienceSignUpInvitationCtaVariant === CTA_VARIANT_GET_AHASLIDES_FOR_FREE) {
        return this.$t('love_to_create_interactive_presentations')
      }
      return null
    },
    ctaButtonLabel() {
      return this.audienceSignUpInvitationCtaVariant === CTA_VARIANT_HOST_EVENT_FOR_FREE
        ? this.$t('host_events_with_ahaslides_free')
        : this.$t('get_ahaslides_free')
    },
    isShownCTAButtons() {
      return this.isEnableFeedback || this.isEnableSlideReview
    },
    isEnableAudienceAhaSlideLabel() {
      return this.presentation && this.presentation.isEnableAudienceAhaSlideLabel
    },
    isShowAhaSection() {
      return this.isEnableAudienceAhaSlideLabel
    },
    isDisabledFeedbackButton() {
      return !this.presentation || !this.presentation.isEnablePrivateFeedback
    },
    trialLink() {
      const presentationId = this.presentation && this.presentation.id
      const params = new URLSearchParams({
        source: 'audience_app',
        presentationid: presentationId,
        campaign: 'audience_app',
      })

      return `${config.presenterUrl}/pages/register?${params.toString()}`
    },
  },
  methods: {
    ...mapActions([
      'setMaxLookbackSlideIndexAction',
    ]),
    openTabLookback() {
      this.setMaxLookbackSlideIndexAction(this.slides.length - 1)

      this.$router.push({
        name: 'lookback',
        params: {
          presentationId: this.$route.params.id,
        },
        query: {
          'lookback-tab': 'slides',
        },
      })

      util.ga4Track('Presentation > Review slides', {
        presentationId: this.presentation && this.presentation.id,
      })
    },
    openTabFeedback() {
      this.$router.push({
        name: 'feedback',
        params: {
          presentationId: this.$route.params.id,
        },
      })
      util.ga4Track('Presentation > Give feedback', {
        presentationId: this.presentation && this.presentation.id,
      })
    },
    openTrialLink() {
      const presentationId = this.presentation && this.presentation.id
      const eventName = this.audienceSignUpInvitationCtaVariant
        ? 'click_audience_sign_up_invitation'
        : 'Presentation > AhaSlides for free'
      util.ga4Track(eventName, {
        presentationId,
      })
      window.open(this.trialLink, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
body.is-audience-paced {
  .audience-cta-wrapper {
    padding-bottom: var(--h-audience-paced);
  }
}

.audience-cta-wrapper {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  text-decoration: none;
  background-color: #F0F4FA;
  text-align: center;
  color: #282E3E;
}

.container {
  padding: 32px 16px 24px 16px;
  max-width: 392px;
  margin: 0 auto;

  @media screen and (min-width: 600px) {
    max-width: 600px;
  }
}

.top-msg {
  margin: 0;
  font-weight: 700;
  font-size: 1.125rem;
}

.sub-msg {
  margin: 6px 0 0;
  font-size: 0.875rem;
  color: #5A6373;
}

.aha-primary-cta {
  margin-top: 20px;
}

.cta-button-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px 24px;
  background-color: #6A1EBB;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 1.0625rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #5a19a0;
  }

  &:active {
    background-color: #4d1589;
  }

  .btn-aha-logo {
    flex-shrink: 0;
    color: #fff;
  }
}

.or-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: #8A94A6;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #D1D9E6;
  }
}

.audience-cta-buttons {
  text-align: center;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  column-gap: 8px;
  row-gap: 12px;

  &--gap {
    margin-top: 20px;
  }
}

.audience-cta-link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 0;
  margin-top: 16px;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  color: inherit;

  .aha-icon-wrapper {
    width: 26px;
    height: 26px;

    .img-link {
      width: 26px;
      height: 26px;
    }
  }

  .text-link {
    font-size: 0.875rem;
    font-weight: 600;
    margin-left: 8px;
    margin-bottom: -2px;
    text-decoration: underline;
  }
}
</style>
